/* Layer configuration, source loading, bbox and masking. Expects `turf` and the page-level `AOIColor` map as globals. */

export function generateAOIConfig(layer, borderColor) {
  let { id, settings, geom } = layer;

  try {
    geom = JSON.parse(geom);
  } catch (e) {
    // geom is already parsed
  }

  let config = {
    'id': id + '',
    type: 'line',
    'source': {
      'type': 'geojson',
      'data': {
        'type': 'Feature',
        'geometry': geom
      }
    },
    'layout': {},
    paint: {
      'line-color': '#000',
      'line-width': 3,
      'line-offset': 0
    }
  };

  if (AOIColor[id]) {
    config.type = 'line';
    config.paint = {
      'line-color': AOIColor[id] || '#000',
      'line-width': 2,
      'line-offset': 1
    };
  } else if (borderColor.color) {
    config.type = 'fill';
    config.paint = {
      'fill-color': '#' + borderColor.color,
      'fill-opacity': 0.2,
      'fill-outline-color': '#333',
    };
  }

  return config;
}

export function generateConfig(layer, config) {
  let { id, settings, geom } = layer;

  if (!config) {
    try {
      geom = JSON.parse(geom);
    } catch (e) {
      // geom is already parsed
    }

    config = {
      'id': id + '',
      'type': 'fill',
      'source': {
        'type': 'geojson',
        'data': {
          'type': 'Feature',
          'geometry': geom
        }
      },
      'layout': {},
      'paint': {
        'fill-color': '#088',
        'fill-opacity': 0.8,
        'fill-outline-color': 'rgba(169,169,169,1)'
      }
    };
  }

  Object.keys(settings || {}).forEach(key => {
    switch (key) {
      case 'color':
        config.paint['fill-color'] = '#' + settings[key];
        break;
      case 'opacity':
      case 'fill_opacity':
        if (!isNaN(parseFloat(settings[key]))) {
          config.paint['fill-opacity'] = (parseFloat(settings[key]) / 100);
        }
        break;
      default:
        break;
    }
  });

  Object.keys(settings?.appearance || {}).forEach(key => {
    const enabled = settings?.appearance[key];
    switch (key) {
      case 'fill':
        if (!enabled) {
          config.paint['fill-opacity'] = 0;
        }
        break;
      case 'border':
        config.paint['fill-outline-color'] = enabled ? 'rgba(169,169,169,0)' : 'rgba(169,169,169,1)';
        break;
      default:
        break;
    }
  });

  return config;
}

export function generateCustomFillConfig(map, layer, borderColor, fillImageUrl) {
  let { id, settings, geom } = layer;

  try {
    geom = JSON.parse(geom);
  } catch (e) {
    // geom is already parsed
  }

  map.loadImage(fillImageUrl, (err, image) => {
    if (err) {
      console.error('Error loading fill image:', err);
      return;
    }

    map.addImage('pattern', image);

    map.addLayer({
      'id': id + 'aoifill',
      'type': 'fill',
      'source': {
        'type': 'geojson',
        'data': {
          'type': 'Feature',
          'geometry': geom
        }
      },
      'paint': {
        'fill-pattern': 'pattern'
      }
    });
  });

  return generateAOIConfig(layer, borderColor);
}

// OPTIMIZED: Event-based loading instead of polling with performance tracking
export function checkSourceLoadedOptimized(id, map) {
  return new Promise((resolve, reject) => {
    const loadStartTime = performance.now();

    // Check if already loaded
    if (map.getSource(id) && map.isSourceLoaded(id)) {
      resolve(id);
      return;
    }

    let timeout;
    let checkCount = 0;

    // Use map events instead of polling
    const onSourceData = (e) => {
      checkCount++;
      if (e.sourceId === id && e.isSourceLoaded) {
        const loadEndTime = performance.now();
        cleanup();
        resolve(id);
      }
    };

    const cleanup = () => {
      map.off('sourcedata', onSourceData);
      if (timeout) clearTimeout(timeout);
    };

    // Reduced timeout for faster failure detection
    timeout = setTimeout(() => {
      const loadEndTime = performance.now();
      console.warn(`[Performance] Source ${id} timeout after ${(loadEndTime - loadStartTime).toFixed(2)}ms and ${checkCount} checks`);
      cleanup();
      if (!map.getSource(id)) {
        reject(new Error(`Source ${id} not found after timeout`));
      } else {
        // Resolve anyway if source exists but didn't fire loaded event
        resolve(id);
      }
    }, 15000); // Reduced from 30s to 15s

    map.on('sourcedata', onSourceData);
  });
}

export function zoomToLayer(map, mapLayers, layerId) {
  return new Promise((resolve) => {
    if (layerId && mapLayers[layerId]) {
      let bbox;

      if (mapLayers[layerId].bbox) {
        bbox = mapLayers[layerId].bbox;
      } else {
        let geoms = Object.values(mapLayers[layerId].layer).map(layer =>
          ({ type: 'Feature', geometry: layer.geom })
        );
        bbox = turf.bbox(geoms[0]);
        mapLayers[layerId].bbox = bbox;
      }

      map.fitBounds(bbox);
    }
    resolve();
  });
}

export function findLayersBBox(mapLayers, layerIds, customBbox) {
  return new Promise((resolve) => {
    if (customBbox != null) {
      resolve(customBbox);
      return;
    }

    if (layerIds == null || layerIds.length === 0) {
      resolve();
      return;
    }

    const bboxes = [];
    let geoms = [];
    layerIds.forEach(l => {
      const layerEntry = mapLayers[l];
      if (!layerEntry) {
        return;
      }

      const entryBbox = layerEntry.bbox ?? layerEntry.full?.bbox ?? layerEntry.full?.settings?.bbox;
      if (entryBbox) {
        bboxes.push(entryBbox);
        return;
      }

      const layerItems = Array.isArray(layerEntry.layer) ? layerEntry.layer : [layerEntry.layer];
      layerItems
        .filter(x => x?.geom)
        .forEach(x => {
          geoms.push({ type: 'Feature', geometry: x.geom });
        });
    });

    if (bboxes.length > 0) {
      const combinedBbox = bboxes.reduce((acc, bbox) => {
        if (!acc) return [...bbox];
        return [
          Math.min(acc[0], bbox[0]),
          Math.min(acc[1], bbox[1]),
          Math.max(acc[2], bbox[2]),
          Math.max(acc[3], bbox[3]),
        ];
      }, null);
      resolve(combinedBbox);
      return;
    }

    if (geoms.length === 0) {
      resolve();
      return;
    }

    resolve(turf.bbox({ type: 'FeatureCollection', features: geoms }));
  });
}

// Turf 7 takes a FeatureCollection for union/difference; Turf 5/6 take separate
// features (and Turf 6 union requires exactly two), so try the Turf 7 form first.
function unionFeatures(features) {
  if (features.length === 1) {
    return features[0];
  }
  try {
    const result = turf.union(turf.featureCollection(features));
    if (result) return result;
  } catch (e) {
    // Turf 5/6
  }
  return features.reduce((acc, feature) => turf.union(acc, feature));
}

function differenceFeatures(feature, toRemove) {
  try {
    const result = turf.difference(turf.featureCollection([feature, toRemove]));
    if (result) return result;
  } catch (e) {
    // Turf 5/6
  }
  return turf.difference(feature, toRemove);
}

export function drawMask(map, layers, maskColor = 'white', maskOpacity = 1) {
  return new Promise((resolve) => {
    if (layers == null || layers.length === 0) {
      resolve();
      return;
    }

    let geoms = Object.values(layers).map(layer =>
      ({ type: 'Feature', geometry: layer.geom })
    );

    if (geoms.length === 0) {
      resolve();
      return;
    }

    const combined = unionFeatures(geoms);
    const world = turf.bboxPolygon([-180, -90, 180, 90]);
    const diff = differenceFeatures(world, combined);

    let config = {
      'id': 'world',
      'source': {
        'type': 'geojson',
        'data': diff
      },
      'type': 'fill',
      'visibility': 'visible',
      'paint': {
        'fill-color': maskColor,
        'fill-opacity': maskOpacity
      }
    };

    map.addLayer(config);
    resolve();
  });
}
